import { useState } from "react";
import Validation from "../../../../utils/validation";
import AmountToWords from "../../../_utils/AmountToWords";
import SingleSelectDropDownComponent from "../../../_utils/CommonDropdown/SingleSelectDropdown";
import Amount from "../../../_utils/TransactionCommonComponents/Amount";
import "./index.css";
import { GlobalConfiguration } from "../../../../utils/configurationProvider";

/**
 * TextAuthMatrixSelectTxnAmount - Component for selecting transaction type and entering amount
 * for testing authorization matrix configuration.
 *
 * This component provides:
 * - A dropdown to select transaction type (Beneficiary/Internal/Private)
 * - An amount input field with real-time conversion to words
 * - Validation for amount input (no leading zeros, allowed characters, max limits)
 * - Automatic "rupee/rupees" singular/plural handling in words
 *
 * All state is managed by the parent component via props - this is a controlled component.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.showRupeeSymbol - Whether to show rupee symbol in amount input
 * @param {Function} props.setShowRupeeSymbol - Setter for showRupeeSymbol
 * @param {string} props.amount - Current amount value (numeric string)
 * @param {Function} props.setAmount - Setter for amount
 * @param {string} props.txnType - Current transaction type (‘Beneficiary’, ‘Internal’, ‘Private’)
 * @param {Function} props.setTxnType - Setter for txnType
 * @param {string} props.amountInWords - Amount converted to words
 * @param {Function} props.setAmountInWords - Setter for amountInWords
 * @returns {JSX.Element} Rendered transaction type selector and amount input
 */
const TextAuthMatrixSelectTxnAmount = ({
  showRupeeSymbol,
  setShowRupeeSymbol,
  amount,
  setAmount,
  txnType,
  setTxnType,
  amountInWords,
  setAmountInWords,
}) => {
  // Tracks dropdown open/close state for SingleSelectDropDownComponent
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  /**
   * Validates if a numeric amount value is allowed.
   * Delegates to Validation.allowedAmount utility.
   */
  const allowedValue = (value) => {
    return Validation.allowedAmount(value);
  };

  /**
   * Handles amount input changes with validation and word conversion.
   *
   * Processing steps:
   * 1. Handle empty input - reset all state
   * 2. Reject values starting with "0" (no leading zeros)
   * 3. Strip non-numeric characters (keep only digits and decimal point)
   * 4. Validate against allowed amount rules (max value, decimal places, etc.)
   * 5. Convert to number and generate words via AmountToWords utility
   * 6. Update all three state values via parent setters
   *
   */
  const handleAmountChange = (val) => {
    // Empty input: clear all related state
    if (!val) {
      setAmount("");
      setAmountInWords("");
      setShowRupeeSymbol(false);
      return;
    }

    // Reject leading zero (e.g., "0123" invalid, "0" valid but caught by allowedValue)
    if (val[GlobalConfiguration?.lengthCheckZero] === "0") return;

    // Sanitize: keep only digits and decimal point
    const numericValue = val.replace(/[^0-9.]/g, "");

    // Validate against business rules (max amount, decimal precision, etc.)
    if (!allowedValue(numericValue)) return;

    const amountNumber = parseFloat(numericValue);

    // Convert to words, handle empty edge case
    const words = !numericValue.trim() ? "" : AmountToWords(amountNumber);

    const adjustedWords =
      amountNumber > GlobalConfiguration?.lengthCheckZero &&
      amountNumber <= GlobalConfiguration?.lengthCheckOne
        ? words.replace("rupees", "rupee")
        : words;

    // Update all controlled state
    setAmount(numericValue);
    setAmountInWords(adjustedWords);
    setShowRupeeSymbol(true);
  };

  return (
    <div className="test-auth-txn-amt">
      <div className="text-auth-txn-div">
        <div className="mb-margin-md flex flex-col gap-gap-xs">
          <div className="text-base font-semibold text-text-primary">
            Transaction Type
          </div>
          <div className="text-xs font-normal text-text-secondary">
            Select the transaction type to fetch your entity’s authorization
            matrix configuration.
          </div>
        </div>
        {/* Transaction Type Dropdown */}
        <SingleSelectDropDownComponent
          type="small"
          options={[
            "Beneficiary transaction",
            "Internal transaction",
            // "Private transaction",
          ]}
          // Map internal txnType values to display strings
          selected={
            txnType === "Beneficiary"
              ? "Beneficiary transaction"
              : txnType === "Private"
                ? "Private transaction"
                : txnType === "Internal"
                  ? "Internal transaction"
                  : ""
          }
          // Map display strings back to internal txnType values
          setSelected={(val) => {
            setTxnType(
              val === "Private transaction"
                ? "Private"
                : val === "Beneficiary transaction"
                  ? "Beneficiary"
                  : val === "Internal transaction"
                    ? "Internal"
                    : "",
            );
          }}
          showSearch={false}
          isOpen={isDropdownOpen}
          placeholder="Select transaction type"
          onOpen={() => {
            setIsDropdownOpen(true);
          }}
          onClose={() => setIsDropdownOpen(false)}
        />
      </div>
      {/* Amount Input with Words Display */}
      <Amount
        title="Amount"
        amount={amount}
        amountInWords={amountInWords}
        onAmountChange={handleAmountChange}
      />
    </div>
  );
};

export default TextAuthMatrixSelectTxnAmount;
